import {
    LIB_IFRAME_ID,
    getLibIframe,
    EXT_LIB_IFRAME_SRC,
    EXT_IFRAME_ALLOW,
    getRecIframe,
} from '../parentConfig'
import { getResourceURL } from '../../services/extServices'
import { createDOMelements } from './createDOMelements'
import { onDragGbtn } from './onDragGbtn'
import { getNotifications } from '../../background/services'
import { createCTAbtns } from './createCTAbtns'
import { locationObserver } from './locationObserver'
import { libIframeMessageReceiver } from './messageReceiver'
import {
    SUPPORTED_NOTIFICATION_TYPES,
    GetOrganizationCbType,
} from '../../types'
import { awaitDOMElement } from '../../services/utils'
import {
    RecordingIframeActionE,
    sendMessageToIframe,
} from '../../services/parentService'
import { toggleLibrary } from './toggleLibrary'
import { captureSentryError } from '../../services/sentry'

export const injectLibIframe = (
    { extLogo, extColor }: GetOrganizationCbType,
    preOpen: boolean = false
) =>
    new Promise<{ iframe: HTMLIFrameElement; container: HTMLElement } | null>(
        // eslint-disable-next-line no-async-promise-executor
        async resolve => {
            const existingIframe = getLibIframe()
            const recIframe = getRecIframe()

            if (Boolean(existingIframe) || !Boolean(recIframe)) {
                resolve({
                    iframe: existingIframe as HTMLIFrameElement,
                    container: (existingIframe as HTMLIFrameElement)
                        .parentElement as HTMLElement,
                })
                return
            }

            const { container, iframe, controlBtn, overlay } =
                createDOMelements()

            iframe.id = LIB_IFRAME_ID
            iframe.src = EXT_LIB_IFRAME_SRC
            iframe.allow = EXT_IFRAME_ALLOW

            const controlBtnImg = document.createElement('img')
            controlBtnImg.src = extLogo || getResourceURL('icons/g-btn.svg')
            controlBtnImg.alt = 'Guidde'
            controlBtnImg.draggable = false
            controlBtnImg.style.height = '100%'
            controlBtnImg.style.backgroundColor = extColor || 'transparent'
            controlBtnImg.style.borderRadius = '12px 0 0 12px'
            controlBtnImg.style.boxShadow = '0 2px 14px 0 rgb(0 0 0 / 30%)'
            onDragGbtn({
                iframe,
                controlBtn,
                container,
                dragHandle: controlBtnImg,
            })

            const notificationsBadge = document.createElement('div')
            notificationsBadge.className = 'Guidde_NotificationsCounter'

            controlBtn.appendChild(controlBtnImg)
            controlBtn.appendChild(notificationsBadge)

            const onCTAClick = () => {
                sendMessageToIframe(
                    recIframe as HTMLIFrameElement,
                    RecordingIframeActionE.OPEN_SETTINGS
                )
                ;(recIframe as HTMLIFrameElement).style.display = 'block'
            }

            const CTA_BUTTONS = [
                {
                    id: 'GUIDDE-JS-Record',
                    icon: 'cta-record.svg',
                    title: 'Record a Video',
                    onClick: () => onCTAClick(),
                },
            ]

            const buttonsContainer = document.createElement('g-buttons')
            buttonsContainer.id = 'GUIDDE-JS-GBtns'
            buttonsContainer.style.display = 'none'
            buttonsContainer.style.position = 'absolute'
            buttonsContainer.style.top = '100%'
            buttonsContainer.style.right = '0'
            buttonsContainer.style.opacity = '0'
            buttonsContainer.style.transition = 'opacity .6s'
            controlBtn.appendChild(buttonsContainer)

            const ctaContainer = createCTAbtns(
                'cta-container',
                'GUIDDE-JS-CTA',
                CTA_BUTTONS
            )

            buttonsContainer.appendChild(ctaContainer)

            let animationTimeout: ReturnType<typeof setTimeout> | null = null
            container.addEventListener('mouseenter', () => {
                if (animationTimeout) {
                    window.clearTimeout(animationTimeout)
                }

                buttonsContainer.style.display = 'block'
                buttonsContainer.style.opacity = '1'
            })

            container.addEventListener('mouseleave', () => {
                buttonsContainer.style.opacity = '0'
                animationTimeout = setTimeout(() => {
                    buttonsContainer.style.display = 'none'
                }, 700)
            })

            // We inject extension to HTML to avoid <body> tag compromising our styles for the container
            const html = await awaitDOMElement('html', 10)
            if (html) {
                html.appendChild(container)
                html.appendChild(overlay)

                if (preOpen) {
                    toggleLibrary(container, iframe, true)
                }

                resolve({ iframe, container })
            } else {
                captureSentryError(
                    new Error('Could not find <html> to inject.')
                )
                resolve(null)
                return
            }

            // Detects URL changes and sends new URL to React
            locationObserver(iframe)

            libIframeMessageReceiver({
                iframe,
                overlay,
                controlBtn,
                container,
            })

            // Add notification indicator if there are new notifications
            getNotifications(notifications => {
                const newSupportedNotifications = notifications.filter(
                    ({ type, status }) =>
                        status === 'new' &&
                        SUPPORTED_NOTIFICATION_TYPES.includes(type)
                )

                if (newSupportedNotifications.length) {
                    notificationsBadge.style.display = 'block'
                } else {
                    notificationsBadge.style.display = 'none'
                    notificationsBadge.innerHTML = ''
                }
            }, {})
        }
    )
